import { useMemo } from "react";
import { useWorkspaceDomainKnowledge, useWorkspaceRiskCatalog, useWorkspaceRisksList } from "@/api/workspace-risks";
import { RiskGroupAccordion } from "@/components/safety-config/RiskGroupAccordion";
import { buildRiskGroupsFromCatalog } from "@/components/safety-config/risk-groups";
import { Skeleton } from "@/components/ui/skeleton";
import { buildWorkspaceRiskState } from "./workspace-risks-csv";

// One placeholder per catalog category, so the loading state matches the number
// of group rows the accordion settles into.
const SKELETON_ROW_KEYS = ["baseline", "claims", "third-parties", "participants", "product", "music", "authenticity"];

const WorkspaceRisksSkeleton = () => (
  <div className="flex flex-col gap-3">
    {SKELETON_ROW_KEYS.map((key) => (
      <div key={key} className="rounded-md border border-border-primary bg-white flex items-center gap-4 px-6 py-5">
        <Skeleton className="w-5 h-5 rounded-full shrink-0" />
        <Skeleton className="h-4 w-44" />
        <div className="flex-1" />
        <Skeleton className="h-4 w-28" />
        <Skeleton className="w-6 h-6 rounded-full shrink-0" />
      </div>
    ))}
  </div>
);

export const WorkspaceRisksTab = ({ workspaceId }: { workspaceId: string }) => {
  const { data: catalog, isLoading: catalogLoading } = useWorkspaceRiskCatalog(workspaceId);
  const { data: workspaceRisksList, isLoading: risksLoading } = useWorkspaceRisksList(workspaceId);
  const { data: domainKnowledge } = useWorkspaceDomainKnowledge(workspaceId);

  const groups = useMemo(() => buildRiskGroupsFromCatalog(catalog ?? []), [catalog]);
  const getRiskState = buildWorkspaceRiskState(workspaceRisksList ?? [], domainKnowledge ?? {});

  if (catalogLoading || risksLoading) return <WorkspaceRisksSkeleton />;

  return (
    <div className="flex flex-col gap-4">
      <RiskGroupAccordion groups={groups} getRiskState={getRiskState} wording="defaults" />
    </div>
  );
};
